import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, RefreshCw, Zap } from "lucide-react";
import { toast } from "sonner";

export function CatalogTab() {
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null); // null | 'new' | plan doc
  const [editPack, setEditPack] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, k] = await Promise.all([
        api.get("/api/admin/plans"),
        api.get("/api/admin/packs"),
      ]);
      setPlans(p.data);
      setPacks(k.data);
    } catch (e) { toast.error("Failed to load catalog"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const syncPlan = async (id) => {
    try {
      const { data } = await api.post(`/api/admin/plans/${id}/sync-stripe`);
      toast.success(`Synced to Stripe (${data.product_id.slice(-8)})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
  };
  const syncPack = async (id) => {
    try {
      const { data } = await api.post(`/api/admin/packs/${id}/sync-stripe`);
      toast.success(`Synced to Stripe (${data.product_id.slice(-8)})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
  };
  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const { data } = await api.post("/api/admin/catalog/sync-all");
      const failed = [...data.plans, ...data.packs].filter(r => !r.ok);
      if (failed.length) toast.error(`${failed.length} item(s) failed to sync`);
      else toast.success(`Synced ${data.plans.length + data.packs.length} items to Stripe`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync all failed"); }
    finally { setSyncingAll(false); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm(`Delete plan "${id}"? Existing subscriptions on this plan are unaffected.`)) return;
    try { await api.delete(`/api/admin/plans/${id}`); toast.success("Plan deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const deletePack = async (id) => {
    if (!window.confirm(`Delete pack "${id}"? Existing user credits are unaffected.`)) return;
    try { await api.delete(`/api/admin/packs/${id}`); toast.success("Pack deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="p-16 flex justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-10">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow text-muted-foreground mb-1">Catalog</div>
          <div className="font-heading text-2xl">Plans · Packs · Stripe sync</div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="admin-sync-all-btn"
            onClick={syncAll}
            disabled={syncingAll}
            variant="outline"
            className="rounded-none border-foreground"
          >
            {syncingAll ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
            Sync all to Stripe
          </Button>
        </div>
      </div>

      {/* PLANS TABLE */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow text-muted-foreground">Subscription plans · {plans.length}</div>
          <Button data-testid="admin-add-plan" onClick={() => setEditPlan("new")} size="sm" className="rounded-none bg-foreground text-background hover:bg-foreground/90">
            <Plus className="w-3.5 h-3.5 mr-1" />Add plan
          </Button>
        </div>
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="p-3 eyebrow text-muted-foreground font-normal">ID</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">Name</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">$/mo</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">$/yr</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">Quota</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">Lookup (mo/yr)</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">Flags</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody data-testid="admin-plans-table">
              {plans.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0" data-testid={`plan-row-${p.id}`}>
                  <td className="p-3 font-mono text-xs">{p.id}</td>
                  <td className="p-3">{p.name}</td>
                  <td className="p-3 font-mono">{p.price_monthly < 0 ? "—" : `$${p.price_monthly}`}</td>
                  <td className="p-3 font-mono">{p.price_annual < 0 ? "—" : `$${p.price_annual}`}</td>
                  <td className="p-3 font-mono">{p.link_quota < 0 ? "∞" : p.link_quota}</td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">
                    {p.lookup_key_monthly || "—"}<br/>{p.lookup_key_annual || "—"}
                  </td>
                  <td className="p-3 text-xs">
                    {p.popular && <span className="inline-block px-1.5 py-0.5 border border-accent text-accent mr-1">popular</span>}
                    {p.is_enterprise && <span className="inline-block px-1.5 py-0.5 border border-border text-muted-foreground">enterprise</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      {!p.is_enterprise && p.id !== "free" && (
                        <button data-testid={`sync-plan-${p.id}`} onClick={() => syncPlan(p.id)} className="p-1.5 hover:bg-secondary border border-transparent hover:border-border" title="Sync to Stripe"><Zap className="w-3.5 h-3.5" /></button>
                      )}
                      <button data-testid={`edit-plan-${p.id}`} onClick={() => setEditPlan(p)} className="p-1.5 hover:bg-secondary border border-transparent hover:border-border"><Pencil className="w-3.5 h-3.5" /></button>
                      {p.id !== "free" && (
                        <button data-testid={`del-plan-${p.id}`} onClick={() => deletePlan(p.id)} className="p-1.5 hover:bg-secondary border border-transparent hover:border-destructive text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PACKS TABLE */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow text-muted-foreground">Lifetime packs · {packs.length}</div>
          <Button data-testid="admin-add-pack" onClick={() => setEditPack("new")} size="sm" className="rounded-none bg-foreground text-background hover:bg-foreground/90">
            <Plus className="w-3.5 h-3.5 mr-1" />Add pack
          </Button>
        </div>
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="p-3 eyebrow text-muted-foreground font-normal">ID</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">Name</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">Size</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">Price</th>
                <th className="p-3 eyebrow text-muted-foreground font-normal">Lookup key</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody data-testid="admin-packs-table">
              {packs.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0" data-testid={`pack-admin-row-${p.id}`}>
                  <td className="p-3 font-mono text-xs">{p.id}</td>
                  <td className="p-3">{p.name}</td>
                  <td className="p-3 font-mono">{p.pack_size}</td>
                  <td className="p-3 font-mono">${p.price}</td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{p.lookup_key}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid={`sync-pack-${p.id}`} onClick={() => syncPack(p.id)} className="p-1.5 hover:bg-secondary border border-transparent hover:border-border" title="Sync to Stripe"><Zap className="w-3.5 h-3.5" /></button>
                      <button data-testid={`edit-pack-${p.id}`} onClick={() => setEditPack(p)} className="p-1.5 hover:bg-secondary border border-transparent hover:border-border"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`del-pack-${p.id}`} onClick={() => deletePack(p.id)} className="p-1.5 hover:bg-secondary border border-transparent hover:border-destructive text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editPlan && <PlanEditor plan={editPlan} onClose={() => setEditPlan(null)} onSaved={load} />}
      {editPack && <PackEditor pack={editPack} onClose={() => setEditPack(null)} onSaved={load} />}
    </div>
  );
}

function PlanEditor({ plan, onClose, onSaved }) {
  const isNew = plan === "new";
  const [f, setF] = useState(() => isNew ? {
    id: "", name: "", price_monthly: 0, price_annual: 0, link_quota: 0,
    lookup_key_monthly: "", lookup_key_annual: "",
    features: [], popular: false, is_enterprise: false, sort_order: 99,
  } : { ...plan });
  const [saving, setSaving] = useState(false);
  const [featuresText, setFeaturesText] = useState((plan?.features || []).join("\n"));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        features: featuresText.split("\n").map(s => s.trim()).filter(Boolean),
        lookup_key_monthly: f.lookup_key_monthly || null,
        lookup_key_annual: f.lookup_key_annual || null,
      };
      if (isNew) await api.post("/api/admin/plans", payload);
      else await api.patch(`/api/admin/plans/${f.id}`, payload);
      toast.success(isNew ? "Plan created" : "Plan updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border border-border sm:max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="plan-editor-dialog">
        <DialogHeader className="p-6 border-b border-border">
          <div className="eyebrow text-muted-foreground mb-2">{isNew ? "New plan" : `Edit ${plan.id}`}</div>
          <DialogTitle className="font-heading text-xl font-light">{isNew ? "Create plan" : "Update plan"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="ID (slug)" testid="plan-id-input"><Input required disabled={!isNew} value={f.id} onChange={(e) => setF({ ...f, id: e.target.value })} className="rounded-none font-mono text-xs" /></Field>
            <Field label="Name" testid="plan-name-input"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none" /></Field>
            <Field label="Price monthly ($)" testid="plan-price-mo-input"><Input type="number" step="0.01" value={f.price_monthly} onChange={(e) => setF({ ...f, price_monthly: parseFloat(e.target.value) || 0 })} className="rounded-none font-mono" /></Field>
            <Field label="Price annual ($)" testid="plan-price-yr-input"><Input type="number" step="0.01" value={f.price_annual} onChange={(e) => setF({ ...f, price_annual: parseFloat(e.target.value) || 0 })} className="rounded-none font-mono" /></Field>
            <Field label="Link quota (-1 unlimited)" testid="plan-quota-input"><Input type="number" value={f.link_quota} onChange={(e) => setF({ ...f, link_quota: parseInt(e.target.value) || 0 })} className="rounded-none font-mono" /></Field>
            <Field label="Sort order" testid="plan-sort-input"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: parseInt(e.target.value) || 0 })} className="rounded-none font-mono" /></Field>
            <Field label="Lookup key (monthly)" testid="plan-lookup-mo-input"><Input placeholder="e.g. basic_monthly" value={f.lookup_key_monthly || ""} onChange={(e) => setF({ ...f, lookup_key_monthly: e.target.value })} className="rounded-none font-mono text-xs" /></Field>
            <Field label="Lookup key (annual)" testid="plan-lookup-yr-input"><Input placeholder="e.g. basic_annual" value={f.lookup_key_annual || ""} onChange={(e) => setF({ ...f, lookup_key_annual: e.target.value })} className="rounded-none font-mono text-xs" /></Field>
          </div>
          <Field label="Features (one per line)"><Textarea rows={5} data-testid="plan-features-input" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} className="rounded-none font-mono text-xs" /></Field>
          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="plan-popular-switch" checked={f.popular} onCheckedChange={(v) => setF({ ...f, popular: v })} /> Popular</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="plan-enterprise-switch" checked={f.is_enterprise} onCheckedChange={(v) => setF({ ...f, is_enterprise: v })} /> Enterprise (contact sales)</label>
          </div>
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-border flex-1">Cancel</Button>
            <Button type="submit" data-testid="plan-save-btn" disabled={saving} className="rounded-none bg-foreground text-background hover:bg-foreground/90 flex-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PackEditor({ pack, onClose, onSaved }) {
  const isNew = pack === "new";
  const [f, setF] = useState(() => isNew ? {
    id: "", name: "", pack_size: 10, price: 9.99, lookup_key: "", sort_order: 99,
  } : { ...pack });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) await api.post("/api/admin/packs", f);
      else await api.patch(`/api/admin/packs/${f.id}`, f);
      toast.success(isNew ? "Pack created" : "Pack updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border border-border sm:max-w-md p-0" data-testid="pack-editor-dialog">
        <DialogHeader className="p-6 border-b border-border">
          <div className="eyebrow text-muted-foreground mb-2">{isNew ? "New pack" : `Edit ${pack.id}`}</div>
          <DialogTitle className="font-heading text-xl font-light">{isNew ? "Create pack" : "Update pack"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="ID (slug)" testid="pack-id-input"><Input required disabled={!isNew} value={f.id} onChange={(e) => setF({ ...f, id: e.target.value })} className="rounded-none font-mono text-xs" /></Field>
            <Field label="Name" testid="pack-name-input"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none" /></Field>
            <Field label="Pack size (links)" testid="pack-size-input"><Input required type="number" value={f.pack_size} onChange={(e) => setF({ ...f, pack_size: parseInt(e.target.value) || 0 })} className="rounded-none font-mono" /></Field>
            <Field label="Price ($)" testid="pack-price-input"><Input required type="number" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: parseFloat(e.target.value) || 0 })} className="rounded-none font-mono" /></Field>
            <Field label="Lookup key" testid="pack-lookup-input"><Input required placeholder="e.g. pack_10" value={f.lookup_key} onChange={(e) => setF({ ...f, lookup_key: e.target.value })} className="rounded-none font-mono text-xs" /></Field>
            <Field label="Sort order" testid="pack-sort-input"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: parseInt(e.target.value) || 0 })} className="rounded-none font-mono" /></Field>
          </div>
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-border flex-1">Cancel</Button>
            <Button type="submit" data-testid="pack-save-btn" disabled={saving} className="rounded-none bg-foreground text-background hover:bg-foreground/90 flex-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-field` : undefined}>
      <span className="eyebrow text-muted-foreground block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
